/**
 * Product Card Quick Buy & Size Selector Component
 * Handles size selection, variant updates, Buy Now checkout redirect, and Add to Cart drawer trigger.
 */
if (!customElements.get('product-card-buy')) {
  customElements.define(
    'product-card-buy',
    class ProductCardBuy extends HTMLElement {
      constructor() {
        super();
        this.selectedVariantId = null;
        this.variants = [];
      }

      connectedCallback() {
        const variantsScript = this.querySelector('.product-card-variants-json');
        if (variantsScript) {
          try {
            this.variants = JSON.parse(variantsScript.textContent || '[]');
          } catch (e) {
            console.error('Error parsing product variants JSON', e);
            this.variants = [];
          }
        }

        const initialVariantId = this.dataset.variantId || (this.variants[0] ? this.variants[0].id : null);
        this.selectedVariantId = initialVariantId;

        this.sizePills = this.querySelectorAll('.product-item__size-pill');
        this.btnBuyNow = this.querySelector('.product-item__btn-buy-now');
        this.btnCartIcon = this.querySelector('.product-item__btn-cart-icon');
        this.priceContainer = this.closest('.product-item')?.querySelector('.card-product-price');

        this.initEvents();
      }

      initEvents() {
        if (this.sizePills && this.sizePills.length > 0) {
          this.sizePills.forEach((pill) => {
            pill.addEventListener('click', this.onSizeClick.bind(this));
          });
        }

        if (this.btnBuyNow) {
          this.btnBuyNow.addEventListener('click', this.onBuyNow.bind(this));
        }

        if (this.btnCartIcon) {
          this.btnCartIcon.addEventListener('click', this.onAddToCart.bind(this));
        }
      }

      onSizeClick(e) {
        e.preventDefault();
        const pill = e.currentTarget;
        if (!pill) return;

        const sizeVal = pill.dataset.value;
        const targetVariantId = pill.dataset.variantId;

        // Find variant matching this size
        let matchedVariant = null;
        if (targetVariantId) {
          matchedVariant = this.variants.find((v) => v.id == targetVariantId);
        }

        if (!matchedVariant && sizeVal) {
          matchedVariant = this.variants.find(
            (v) =>
              (v.option1 === sizeVal || v.option2 === sizeVal || v.option3 === sizeVal) && v.available
          );
          if (!matchedVariant) {
            matchedVariant = this.variants.find(
              (v) => v.option1 === sizeVal || v.option2 === sizeVal || v.option3 === sizeVal
            );
          }
        }

        if (matchedVariant) {
          this.selectedVariantId = matchedVariant.id;
          this.dataset.variantId = matchedVariant.id;

          // Update active pill
          this.sizePills.forEach((p) => p.classList.remove('active'));
          pill.classList.add('active');

          // Update action buttons availability
          this.updateButtons(matchedVariant);

          // Update price display if needed
          this.updatePrice(matchedVariant);
        }
      }

      updateButtons(variant) {
        if (!variant) return;

        if (this.btnBuyNow) {
          this.btnBuyNow.dataset.variantId = variant.id;
          const textSpan = this.btnBuyNow.querySelector('.btn-text');
          if (variant.available) {
            this.btnBuyNow.removeAttribute('disabled');
            if (textSpan) textSpan.textContent = 'Buy Now';
          } else {
            this.btnBuyNow.setAttribute('disabled', 'disabled');
            if (textSpan) textSpan.textContent = 'Sold Out';
          }
        }

        if (this.btnCartIcon) {
          this.btnCartIcon.dataset.variantId = variant.id;
          if (variant.available) {
            this.btnCartIcon.removeAttribute('disabled');
          } else {
            this.btnCartIcon.setAttribute('disabled', 'disabled');
          }
        }
      }

      updatePrice(variant) {
        if (!this.priceContainer || !variant) return;

        const priceEl = this.priceContainer.querySelector('.price');
        const compareEl = this.priceContainer.querySelector('.compare-price');

        const moneyFormat =
          window.themeGlobalVariables?.settings?.money_format ||
          window.cartStrings?.money_format ||
          'Rs. {{amount}}';

        if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
          if (priceEl) {
            priceEl.innerHTML = Shopify.formatMoney(variant.price, moneyFormat);
          }
          if (compareEl) {
            if (variant.compare_at_price && variant.compare_at_price > variant.price) {
              compareEl.innerHTML = Shopify.formatMoney(variant.compare_at_price, moneyFormat);
              compareEl.closest('.price-regular')?.classList.remove('hidden');
            } else {
              compareEl.closest('.price-regular')?.classList.add('hidden');
            }
          }
        }
      }

      onBuyNow(e) {
        e.preventDefault();
        if (this.btnBuyNow.hasAttribute('disabled') || this.btnBuyNow.classList.contains('loading')) {
          return;
        }

        this.btnBuyNow.classList.add('loading');

        const formData = new FormData();
        formData.append('id', this.selectedVariantId);
        formData.append('quantity', 1);

        const addUrl = window.routes?.cart_add_url || '/cart/add.js';

        fetch(addUrl, {
          method: 'POST',
          body: formData,
          headers: {
            'Accept': 'application/json',
          },
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.status && data.description) {
              if (typeof showToast === 'function') {
                showToast(data.description, 4000, 'modal-error');
              } else {
                alert(data.description);
              }
              this.btnBuyNow.classList.remove('loading');
              return;
            }
            window.location.href = '/checkout';
          })
          .catch((err) => {
            console.error('Buy Now error:', err);
            this.btnBuyNow.classList.remove('loading');
            window.location.href = '/checkout';
          });
      }

      onAddToCart(e) {
        e.preventDefault();
        if (this.btnCartIcon.hasAttribute('disabled') || this.btnCartIcon.classList.contains('loading')) {
          return;
        }

        this.btnCartIcon.classList.add('loading');

        const cartElement =
          document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        const formData = new FormData();
        formData.append('id', this.selectedVariantId);
        formData.append('quantity', 1);

        if (cartElement && typeof cartElement.getSectionsToRender === 'function') {
          formData.append(
            'sections',
            cartElement.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          if (typeof cartElement.setActiveElement === 'function') {
            cartElement.setActiveElement(this.btnCartIcon);
          }
        } else {
          formData.append('sections', 'minicart-form');
          formData.append('sections_url', window.location.pathname);
        }

        const addUrl = window.routes?.cart_add_url || '/cart/add.js';

        fetch(addUrl, {
          method: 'POST',
          body: formData,
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/javascript',
          },
        })
          .then((res) => res.json())
          .then((response) => {
            this.btnCartIcon.classList.remove('loading');

            if (response.status && response.description) {
              if (typeof showToast === 'function') {
                showToast(response.description, 4000, 'modal-error');
              } else {
                alert(response.description);
              }
              return;
            }

            // Update cart counter badges & prices
            fetch('/cart.json')
              .then((res) => res.json())
              .then((cart) => {
                if (cart && cart.item_count !== undefined) {
                  document.querySelectorAll('.cart-count').forEach((el) => {
                    if (el.classList.contains('cart-count-drawer')) {
                      el.innerHTML = `(${cart.item_count})`;
                    } else {
                      el.innerHTML = cart.item_count > 100 ? '~' : cart.item_count;
                    }
                  });
                  const headerTotalPrice = document.querySelector('header-total-price');
                  if (headerTotalPrice && typeof headerTotalPrice.updateTotal === 'function') {
                    headerTotalPrice.updateTotal(cart);
                  }
                  const freeShip = document.querySelector('free-ship-progress-bar');
                  if (freeShip && typeof freeShip.init === 'function') {
                    freeShip.init(cart.items_subtotal_price);
                  }
                }
              })
              .catch((err) => console.error(err));

            // Render minicart sections
            if (response.sections) {
              const elementToReplace = document.getElementById('minicart-form');
              if (elementToReplace && response.sections['minicart-form']) {
                const parsedHtml = new DOMParser().parseFromString(
                  response.sections['minicart-form'],
                  'text/html'
                );
                const inner = parsedHtml.querySelector('#minicart-form');
                if (inner) {
                  elementToReplace.innerHTML = inner.innerHTML;
                } else {
                  elementToReplace.innerHTML = response.sections['minicart-form'];
                }
              }
            }

            // Open the Cart Drawer
            if (cartElement) {
              if (typeof cartElement.cartAction === 'function') {
                cartElement.cartAction();
              }
              if (cartElement.classList.contains('is-empty')) {
                cartElement.classList.remove('is-empty');
              }
              if (typeof cartElement.open === 'function') {
                cartElement.open();
              }
            } else {
              const minicartWrapper = document.querySelector('.minicart__wrapper');
              if (minicartWrapper) {
                minicartWrapper.classList.add('open');
                document.documentElement.classList.add('open-drawer', 'open-minicart');
              }
            }

            if (
              typeof publish === 'function' &&
              typeof PUB_SUB_EVENTS !== 'undefined' &&
              PUB_SUB_EVENTS.cartUpdate
            ) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-card-buy',
                productVariantId: this.selectedVariantId,
                cartData: response,
              });
            }
          })
          .catch((err) => {
            console.error('Add to cart error:', err);
            this.btnCartIcon.classList.remove('loading');
          });
      }
    }
  );
}
